// src/pages/Main/Main.jsx
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import Axios from "axios";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/bundle";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Pagination, Scrollbar, A11y, Autoplay } from "swiper/modules";
import { useNavigate, Link } from "react-router-dom";

import video1 from "../../assets/video/main_video1.mp4";
import spinCard1 from "../../assets/images/spin.png";
import spinCard2 from "../../assets/images/spin_back.png";

import hosp1 from "../../assets/images/hospital1.png";
import hosp2 from "../../assets/images/hospital2.png";
import hosp3 from "../../assets/images/hospital3.png";
import hosp4 from "../../assets/images/hospital4.png";

import partner1 from "../../assets/images/partner1.png";
import partner2 from "../../assets/images/partner2.png";
import partner3 from "../../assets/images/partner3.png";
import partner4 from "../../assets/images/partner4.png";
import partner5 from "../../assets/images/partner5.png";
import partner6 from "../../assets/images/partner6.png";
import partner7 from "../../assets/images/partner7.png";
import partner8 from "../../assets/images/partner8.png";
import partner9 from "../../assets/images/partner9.png";
import partner10 from "../../assets/images/partner10.png";

// ✅ 각 STEP에서 1장씩 쓸 이미지
import step1_home from "../../assets/images/step1_home.png";
import step2_home from "../../assets/images/step2_home.png";
import step3_2 from "../../assets/images/step3_1_home.png";
import step4 from "../../assets/images/step4_home.png";
import step5 from "../../assets/images/step5_home.png";
import step6_1 from "../../assets/images/step6_home.png";

const Main = () => {
  const [newsList, setNewsList] = useState([]);
  const navigate = useNavigate();

  const [viewBox, setViewBox] = useState("0 0 1250 320");
  const firstRef = useRef(null);
  const metricsRef = useRef(null);
  const [isFirstInView, setIsFirstInView] = useState(true);
  const isAutoScrollingRef = useRef(false);

  useEffect(() => {
    const updateViewBox = () => {
      if (window.innerWidth <= 1024) {
        setViewBox("0 0 1400 320"); // ✅ 태블릿 이하일 때 확대
      } else {
        setViewBox("0 0 1250 320"); // ✅ 기본
      }
    };

    updateViewBox(); // 초기 실행
    window.addEventListener("resize", updateViewBox);
    return () => window.removeEventListener("resize", updateViewBox);
  }, []);

  /* ===== News: 5개 페이징 + 선택 ===== */
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(0);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((newsList?.length || 0) / PAGE_SIZE)),
    [newsList]
  );
  const start = page * PAGE_SIZE;
  const visibleNews = useMemo(
    () => (newsList || []).slice(start, start + PAGE_SIZE),
    [newsList, start]
  );
  const [selectedInPage, setSelectedInPage] = useState(0);
  useEffect(() => setSelectedInPage(0), [page]);
  const selectedNews = visibleNews?.[selectedInPage];

  const handlePrev = () => {
    if (!visibleNews.length) return;
    if (totalPages <= 1) {
      setSelectedInPage(
        (i) => (i - 1 + visibleNews.length) % visibleNews.length
      );
    } else {
      setPage((p) => (p - 1 + totalPages) % totalPages);
    }
  };
  const handleNext = () => {
    if (!visibleNews.length) return;
    if (totalPages <= 1) {
      setSelectedInPage((i) => (i + 1) % visibleNews.length);
    } else {
      setPage((p) => (p + 1) % totalPages);
    }
  };
  const handleSelectRow = (i) => setSelectedInPage(i);

  // 첫 섹션 in-view 감지
  useEffect(() => {
    if (!firstRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setIsFirstInView(entry.intersectionRatio >= 0.6);
      },
      { threshold: [0, 0.6, 1] }
    );
    io.observe(firstRef.current);
    return () => io.disconnect();
  }, []);

  // 첫 섹션에서 아래로 스크롤하면 metrics로 스무스 이동
  useEffect(() => {
    const smoothScrollToMetrics = () => {
      if (!metricsRef.current) return;
      const startY = window.scrollY;
      const targetY =
        metricsRef.current.getBoundingClientRect().top + window.scrollY;
      const distance = targetY - startY;
      const duration = 1600;
      const startTime = performance.now();
      const easeInOutCubic = (t) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

      const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeInOutCubic(progress);
        window.scrollTo(0, startY + distance * eased);
        if (progress < 1) requestAnimationFrame(animate);
        else isAutoScrollingRef.current = false;
      };

      isAutoScrollingRef.current = true;
      requestAnimationFrame(animate);
    };

    const onWheel = (e) => {
      if (!isFirstInView) return;
      if (isAutoScrollingRef.current) return;
      if (e.deltaY > 0) {
        e.preventDefault();
        smoothScrollToMetrics();
      }
    };

    let touchStartY = 0;
    const onTouchStart = (e) => {
      touchStartY = e.touches?.[0]?.clientY ?? 0;
    };
    const onTouchMove = (e) => {
      if (!isFirstInView) return;
      if (isAutoScrollingRef.current) return;
      const dy = (e.touches?.[0]?.clientY ?? 0) - touchStartY;
      if (dy < -8) {
        e.preventDefault();
        smoothScrollToMetrics();
      }
    };

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [isFirstInView]);

  const SomeComponent = () => {
    const navigate = useNavigate();

    return (
      <button
        className="pill pill--ghost__right"
        onClick={() => navigate("/medicode", { state: { scrollTo: "bottom" } })}
      >
        제안서 확인
      </button>
    );
  };

  /* ===== 숫자 카운트업 ===== */
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".js-countup"));
    if (!els.length) return;

    let triggered = false;
    const duration = 1200;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animate = (el) => {
      const target = Number(el.getAttribute("data-target")) || 0;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = easeOutCubic(p);
        const value = Math.floor(eased * target);
        el.textContent = value.toLocaleString();
        if (p < 1) requestAnimationFrame(step);
        else el.textContent = target.toLocaleString();
      };
      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!triggered && entry.isIntersecting) {
            triggered = true;
            els.forEach(animate);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );

    const section = document.querySelector(".metrics-section");
    if (section) io.observe(section);
    return () => io.disconnect();
  }, []);

  /* ===== 데이터 로드: 뉴스 ===== */
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await Axios.get(
          "https://www.medicode.co.kr:8445/api/get/news_list"
        );
        setNewsList(res.data || []);
      } catch (e) {
        console.error("뉴스 데이터 가져오기 오류:", e);
      }
    };
    fetchNews();
  }, []);

  // 리빌 컨트롤러
  const lastYRef = useRef(window.scrollY);
  const dirRef = useRef("down");
  const tickingRef = useRef(false);
  const runRevealController = () => {
    const dir = dirRef.current;
    const viewportH = window.innerHeight;
    const triggerLine = viewportH * 0.9;
    const nodes = document.querySelectorAll("[data-reveal]");
    nodes.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (dir === "down") {
        if (
          !el.classList.contains("is-visible") &&
          rect.top <= triggerLine &&
          rect.bottom >= 0
        ) {
          el.classList.add("is-visible");
        }
      } else {
        const once = el.hasAttribute("data-reveal-once");
        if (
          !once &&
          el.classList.contains("is-visible") &&
          rect.top >= viewportH
        ) {
          el.classList.remove("is-visible");
        }
      }
    });
    tickingRef.current = false;
  };
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      dirRef.current = y > lastYRef.current ? "down" : "up";
      lastYRef.current = y;
      if (!tickingRef.current) {
        tickingRef.current = true;
        requestAnimationFrame(runRevealController);
      }
    };
    requestAnimationFrame(runRevealController);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  useEffect(() => {
    requestAnimationFrame(runRevealController);
  }, [newsList.length]);

  /* ===== 라우팅 ===== */
  const handleDetailClick = (menu, idx) => {
    const selectedNewsAll = newsList[idx];
    navigate(`/board/${menu}/${selectedNewsAll.idx}`, {
      state: { selectedNews: selectedNewsAll },
    });
  };

  return (
    <Fragment>
      <main>
        {/* 스크롤 인디케이터 */}
        <div
          className="scroll-indicator"
          onClick={() => {
            if (!metricsRef.current) return;
            const startY = window.scrollY;
            const targetY =
              metricsRef.current.getBoundingClientRect().top + window.scrollY;
            const distance = targetY - startY;
            const duration = 1600;
            const startTime = performance.now();
            const easeInOutCubic = (t) =>
              t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            const animate = (now) => {
              const elapsed = now - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const eased = easeInOutCubic(progress);
              window.scrollTo(0, startY + distance * eased);
              if (progress < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
          }}
        >
          <div className="circle">
            <div className="arrow" />
          </div>
        </div>

        {/* ===== First Section (통합) ===== */}
        <section
          ref={firstRef}
          className="first-section"
          data-reveal-group
          data-reveal-base="60"
          data-reveal-step="80"
        >
          <Swiper
            modules={[Pagination, Scrollbar, A11y, Autoplay]}
            slidesPerView={1}
            pagination={{ clickable: true }}
            scrollbar={{ draggable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            speed={800}
          >
            <SwiperSlide className="swiper-slide video-slide">
              <video
                className="video-bg"
                src={video1}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
              />
              <div className="main-txt-wrap">
                <div className="main-txt1" data-reveal>
                  더 나은 의료서비스
                </div>
                <div className="main-txt2" data-reveal>
                  <div className="slider">
                    <span>병원은 스마트하게</span>
                    <span>예약은 간편하게</span>
                    <span>고객은 만족스럽게</span>
                    <span>모두를 위해 더 편리하게</span>
                  </div>
                </div>
                <div className="main-txt3" data-reveal>
                  더 많은 고객과의 연결
                </div>
                <div className="main-btn1" data-reveal>
                  <button
                    className="cta-btn"
                    onClick={() =>
                      window.open(
                        "https://www.wlcmanager.com/self/1iciadklgCPD",
                        "_blank"
                      )
                    }
                  >
                    <span className="label">예약하기</span>
                    <svg
                      className="icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 5l7 7-7 7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </section>

        {/* ===== Metrics Section ===== */}
        <section
          ref={metricsRef}
          className="metrics-section"
          data-reveal-group
          data-reveal-base="60"
          data-reveal-step="90"
        >
          <div className="metrics-bg" aria-hidden="true" />
          <div className="metrics-inner">
            <div className="metrics-left-right">
              <div className="metrics-card-wrap">
                <div className="metrics-hero-icon" aria-hidden="true">
                  <div className="card3d">
                    <img className="face front" src={spinCard1} alt="front" />
                    <img className="face back" src={spinCard2} alt="back" />
                  </div>
                </div>
              </div>
              <div className="metrics-txt-wrap">
                <h2 className="metrics-title" data-reveal>
                  병원과 고객을 가장 효율적으로 연결해주는 건강검진 예약 플랫폼
                </h2>
                <p className="metrics-sub" data-reveal>
                  메디코드는 디지털 예약시스템을 기반으로
                </p>
                <p className="metrics-sub" data-reveal>
                  신뢰할 수 있는 병원 네트워크와 합리적인 가격을 제공합니다.
                </p>

                <div
                  className="metrics-grid"
                  role="list"
                  data-reveal-group
                  data-reveal-base="80"
                  data-reveal-step="70"
                >
                  <article className="metric-card" role="listitem" data-reveal>
                    <div className="metric-header">
                      <div className="metric-label">거점 협력병원</div>
                    </div>
                    <div className="metric-number">
                      <span className="num js-countup" data-target="4">
                        0
                      </span>
                      <span className="unit">곳</span>
                    </div>
                    <p className="metric-desc">강남·강북·경기 주요 네트워크</p>
                  </article>

                  <article className="metric-card" role="listitem" data-reveal>
                    <div className="metric-header">
                      <div className="metric-label">제휴 파트너</div>
                    </div>
                    <div className="metric-number">
                      <span className="num js-countup" data-target="10">
                        0
                      </span>
                      <span className="unit">개+</span>
                    </div>
                    <p className="metric-desc">
                      웨딩 및 보험·채널사 협업을 통한 안정적 유입 구조
                    </p>
                  </article>

                  <article className="metric-card" role="listitem" data-reveal>
                    <div className="metric-header">
                      <div className="metric-label">검진 완료 고객</div>
                    </div>
                    <div className="metric-number">
                      <span className="num js-countup" data-target="1000">
                        0
                      </span>
                      <span className="unit">명+</span>
                    </div>
                    <p className="metric-desc">
                      시스템을 통한 실제 예약·검진 완료 기준 (2024~현재)
                    </p>
                  </article>

                  <article className="metric-card" role="listitem" data-reveal>
                    <div className="metric-header">
                      <div className="metric-label">평균 비용 절감</div>
                    </div>
                    <div className="metric-number">
                      <span className="num js-countup" data-target="50">
                        0
                      </span>
                      <span className="unit">%</span>
                      <span className="suffix">↓</span>
                    </div>
                    <p className="metric-desc">
                      프리미엄 건강검진을 합리적인 가격에 제공
                    </p>
                  </article>
                </div>
              </div>
            </div>

            <div className="metrics-network" aria-hidden="true">
              <svg viewBox={viewBox} preserveAspectRatio="xMidYMid meet">
                <defs>
                  <linearGradient id="blueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#63b3ed" />
                    <stop offset="100%" stopColor="#2b6cb0" />
                  </linearGradient>
                  <filter
                    id="soft"
                    x="-20%"
                    y="-20%"
                    width="140%"
                    height="140%"
                  >
                    <feGaussianBlur
                      in="SourceAlpha"
                      stdDeviation="2"
                      result="blur"
                    />
                    <feOffset dy="1" result="off" />
                    <feMerge>
                      <feMergeNode in="off" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* 병원 → 허브 (왼쪽 연결선) */}
                <path className="net-line" d="M210,70 L552,150" />

                {/* 파트너 → 허브 (아래쪽 연결선, 그대로 유지 가능) */}
                <path className="net-line" d="M210,250 L552,150" />

                {/* 허브 → 고객 (오른쪽 연결선) */}
                <path className="net-line" d="M648,150 L1000,151" />

                <g className="node hospital" transform="translate(170,70)">
                  {/* 바깥 원 — 살짝 키움 */}
                  <circle
                    r="38"
                    fill="white"
                    stroke="#2b6cb0"
                    strokeWidth="2.5"
                  />

                  {/* 병원 건물 틀 — 살짝 축소 */}
                  <rect
                    x="-16"
                    y="-14"
                    width="32"
                    height="24"
                    rx="4"
                    ry="4"
                    fill="white"
                    stroke="#2b6cb0"
                    strokeWidth="2"
                  />

                  {/* 지붕 */}
                  <rect
                    x="-10"
                    y="-24"
                    width="20"
                    height="8"
                    rx="2"
                    ry="2"
                    fill="white"
                    stroke="#2b6cb0"
                    strokeWidth="1.8"
                  />

                  {/* 십자가 */}
                  <path
                    d="M-5,-4 h10 M0,-9 v10"
                    stroke="#2b6cb0"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />

                  {/* 문 */}
                  <rect
                    x="-5"
                    y="3"
                    width="10"
                    height="12"
                    rx="1.5"
                    fill="white"
                    stroke="#2b6cb0"
                    strokeWidth="1.3"
                  />

                  {/* 원 안쪽 아래 텍스트 */}
                  <text
                    textAnchor="middle"
                    y="29"
                    fontSize="12"
                    fill="#2b6cb0"
                    fontWeight="500"
                  >
                    병원
                  </text>
                </g>

                <g className="node partner" transform="translate(170,250)">
                  {/* 바깥 원 */}
                  <circle
                    r="38"
                    fill="white"
                    stroke="#2b6cb0"
                    strokeWidth="2.5"
                  />

                  {/* 왼쪽 사람 */}
                  <circle
                    cx="-7"
                    cy="-8"
                    r="6"
                    fill="none"
                    stroke="#2b6cb0"
                    strokeWidth="2.3"
                  />
                  <path
                    d="M-13,8 C-11,-2 -3,-2 -1,8"
                    fill="none"
                    stroke="#2b6cb0"
                    strokeWidth="2.3"
                    strokeLinecap="round"
                  />

                  {/* 오른쪽 사람 */}
                  <circle
                    cx="7"
                    cy="-8"
                    r="6"
                    fill="none"
                    stroke="#2b6cb0"
                    strokeWidth="2.3"
                  />
                  <path
                    d="M13,8 C11,-2 3,-2 1,8"
                    fill="none"
                    stroke="#2b6cb0"
                    strokeWidth="2.3"
                    strokeLinecap="round"
                  />

                  {/* 텍스트 */}
                  <text
                    textAnchor="middle"
                    y="26"
                    fontSize="12"
                    fill="#2b6cb0"
                    fontWeight="500"
                  >
                    파트너
                  </text>
                </g>

                <g className="node hub" transform="translate(600,150)">
                  {/* 바깥 원 — 크기 키움 */}
                  <circle
                    r="70"
                    fill="white"
                    stroke="#2b6cb0"
                    strokeWidth="2.5"
                  />

                  {/* MEDICODE 로고 텍스트 */}
                  <text
                    textAnchor="middle"
                    dy="-4"
                    fontSize="17"
                    fontFamily="Pretendard_b"
                    fill="#2b6cb0"
                    fontWeight="700"
                  >
                    MEDICODE
                  </text>

                  {/* 설명 텍스트 */}
                  <text
                    textAnchor="middle"
                    dy="14"
                    fontSize="10.5"
                    fill="#2b6cb0"
                    fontWeight="500"
                  >
                    건강검진 예약 플랫폼
                  </text>
                </g>

                <g className="node user" transform="translate(1040,150)">
                  {/* 바깥 원 */}
                  <circle
                    r="38"
                    fill="white"
                    stroke="#2b6cb0"
                    strokeWidth="2.5"
                  />

                  {/* 머리 */}
                  <circle
                    cx="0"
                    cy="-6"
                    r="8"
                    fill="none"
                    stroke="#2b6cb0"
                    strokeWidth="2"
                  />

                  {/* 몸통 (머리랑 간격 좁힘) */}
                  <path
                    d="M-16,12 C-12,4 12,4 16,12"
                    stroke="#2b6cb0"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />

                  {/* 텍스트 */}
                  <text
                    textAnchor="middle"
                    y="26"
                    fontSize="12"
                    fill="#2b6cb0"
                    fontWeight="500"
                  >
                    고객
                  </text>
                </g>
              </svg>
            </div>
          </div>
        </section>

        {/* ===== 파트너 슬라이드 ===== */}
        <section className="extra-section">
          <div className="metrics-extra">
            <div className="metrics-txt">
              <p className="metrics-eyebrow" data-reveal>
                <span className="why-hero">IN PARTNERSHIP WITH</span>
                <span className="why-lead">
                  가치를 함께 만드는 파트너십. <br />
                  함께하는 협력사들이 곧 우리의 신뢰입니다. <br />
                  당신의 건강 여정, 믿을 수 있는 파트너와 함께합니다.
                </span>
              </p>

              {/* <div className="partner-tagline">
                <span>PARTNERS</span>
                <span>FOR</span>
                <span>HEALTH</span>
              </div> */}

              <div className="metrics-slide-wrap">
                {/* 상단: 협력병원 (왼→오) */}
                <div className="msw-top">
                  <ul className="track auto-x-rev">
                    <li className="item">
                      <img src={hosp1} alt="병원1" />
                    </li>
                    <li className="item">
                      <img src={hosp2} alt="병원2" />
                    </li>
                    <li className="item">
                      <img src={hosp3} alt="병원3" />
                    </li>
                    <li className="item">
                      <img src={hosp4} alt="병원4" />
                    </li>
                    {/* 복제 */}
                    <li className="item">
                      <img src={hosp1} alt="병원1" />
                    </li>
                    <li className="item">
                      <img src={hosp2} alt="병원2" />
                    </li>
                    <li className="item">
                      <img src={hosp3} alt="병원3" />
                    </li>
                    <li className="item">
                      <img src={hosp4} alt="병원4" />
                    </li>
                  </ul>
                </div>

                {/* 하단: 2줄 */}
                <div className="msw-bottom">
                  <div className="msw-line top-line">
                    <ul className="track auto-x">
                      <li className="item">
                        <img src={partner1} alt="업체1" />
                      </li>
                      <li className="item">
                        <img src={partner2} alt="업체2" />
                      </li>
                      <li className="item">
                        <img src={partner3} alt="업체3" />
                      </li>
                      <li className="item">
                        <img src={partner4} alt="업체4" />
                      </li>
                      <li className="item">
                        <img src={partner5} alt="업체5" />
                      </li>
                      {/* 복제 */}
                      <li className="item">
                        <img src={partner1} alt="업체1" />
                      </li>
                      <li className="item">
                        <img src={partner2} alt="업체2" />
                      </li>
                      <li className="item">
                        <img src={partner3} alt="업체3" />
                      </li>
                      <li className="item">
                        <img src={partner4} alt="업체4" />
                      </li>
                      <li className="item">
                        <img src={partner5} alt="업체5" />
                      </li>
                    </ul>
                  </div>

                  <div className="msw-line bottom-line">
                    <ul className="track auto-x-rev">
                      <li className="item">
                        <img src={partner6} alt="업체6" />
                      </li>
                      <li className="item">
                        <img src={partner7} alt="업체7" />
                      </li>
                      <li className="item">
                        <img src={partner8} alt="업체8" />
                      </li>
                      <li className="item">
                        <img src={partner9} alt="업체9" />
                      </li>
                      <li className="item">
                        <img src={partner10} alt="업체10" />
                      </li>
                      {/* 복제 */}
                      <li className="item">
                        <img src={partner6} alt="업체6" />
                      </li>
                      <li className="item">
                        <img src={partner7} alt="업체7" />
                      </li>
                      <li className="item">
                        <img src={partner8} alt="업체8" />
                      </li>
                      <li className="item">
                        <img src={partner9} alt="업체9" />
                      </li>
                      <li className="item">
                        <img src={partner10} alt="업체10" />
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Benefits Section ===== */}
        <section
          className="benefits-section"
          data-reveal-group
          data-reveal-base="60"
          data-reveal-step="90"
        >
          <div className="benefits-inner">
            <p className="benefits-eyebrow" data-reveal>
              <span className="why-hero">WHY MEDICODE</span>
              <span className="why-lead">
                검증된 병원과 합리적이고 믿을 수 있는 검진을,
                <br /> 예약부터 관리까지 한 번에
              </span>
            </p>

            <div
              className="benefits-grid benefits-grid--tiles"
              role="list"
              data-reveal-group
              data-reveal-base="60"
              data-reveal-step="70"
            >
              {[
                {
                  n: "01",
                  over: "웹으로 간편 예약",
                  k: "1min",
                  t: "웹으로 1분 예약",
                  d: "회원가입 없이도 빠르게 예약 완료. \n필요한 정보만 간단히 입력하면 끝!",
                  theme: "-accent",
                },
                {
                  n: "02",
                  over: "합리적인 가격",
                  k: "50%",
                  t: "시중가 대비 합리적",
                  d: "파트너십 기반으로 평균 최대 50% 절감. \n알기 쉬운 패키지 가격.",
                  theme: "-light",
                },
                {
                  n: "03",
                  over: "검증된 네트워크",
                  k: "QC",
                  t: "검증된 병원 네트워크",
                  d: "강남·강북·경기권 거점 병원과 제휴. \n품질 중심으로 선별합니다.",
                  theme: "-dark",
                },
                {
                  n: "04",
                  over: "일정 변경도 간편",
                  k: "Care",
                  t: "변경 요청도 손쉽게",
                  d: "일정 변경이 필요할 땐 담당자가 빠르게 확인 후 조정\n복잡한 절차 없이, 편하게 요청하세요.",
                  theme: "-light",
                },
                {
                  n: "05",
                  over: "진행 상황 공유",
                  k: "Manager",
                  t: "담당자가 전 과정을 관리",
                  d: "담당자가 예약부터 일정 조율까지 관리하며,\n진행 현황은 필요 시 고객님에게 안내드립니다.",
                  theme: "-accent",
                },
              ].map((b, i) => (
                <article
                  className={`benefit-card tile ${b.theme}`}
                  role="listitem"
                  key={b.n}
                  data-reveal
                  data-reveal-index={i}
                >
                  <header className="tile-head">
                    <span className="tile-over">{b.over}</span>
                  </header>
                  <div className="tile-body">
                    <div className="tile-k">{b.k}</div>
                    <h3 className="b-title">{b.t}</h3>
                    <p className="b-desc">{b.d}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 인트로 */}
        <section className="circle-section">
          <div className="circle-han">
            <div className="circle-eyebrow">
              <span className="why-hero">EASY HEALTH CHECK</span>
            </div>
            <h2 className="circle-title" data-reveal>
              복잡한 절차 없이, 건강검진을 쉽고 스마트하게 예약하세요.
            </h2>
            <p className="circle-sub" data-reveal>
              온라인으로 <strong>단 1분 만에</strong> 빠르고 간편하게 예약,
              <br />
              병원 선택부터 일정 관리까지, <strong>온라인으로</strong> 처리할 수
              있어요.
            </p>
          </div>
        </section>

        {/* 1묶음: START / STEP 01 */}
        <section className="dual-showcase" data-reveal="dual">
          <div className="ds-col ds-col--left">
            <div className="ds-text">
              <p className="ds-tag">START</p>
              <h3 className="ds-title">건강검진 예약을 시작해볼까요?</h3>
              <p className="ds-desc">
                대상자 선택부터 병원 일정까지,
                <br /> 단 1분이면 예약이 완료돼요.
              </p>
            </div>
            <div className="ds-media">
              <img src={step1_home} alt="예약 시작 화면" />
            </div>
          </div>

          <div className="ds-col ds-col--right">
            <div className="ds-media">
              <img src={step2_home} alt="병원 또는 검진상품 선택 화면" />
            </div>
            <div className="ds-text">
              <p className="ds-tag">STEP 01</p>
              <p className="ds-desc">
                <strong>검진상품 선택</strong>: 원하는 검진 상품을 먼저 고르고,
                <br />
                해당 상품을 제공하는 병원을 확인할 수 있어요.
                <div className="padding-span"></div>
                <strong>병원 선택</strong>: 원하는 병원을 먼저 고른 뒤,
                <br />그 병원에서 진행 가능한 검진 상품을 볼 수 있어요.
              </p>
            </div>
          </div>
        </section>

        {/* 2묶음: STEP 02 / CHECK LIST */}
        <div style={{ background: "#f9fafb" }}>
          <section className="dual-showcase" data-reveal="dual">
            <div className="ds-col ds-col--left">
              <div className="ds-text">
                <p className="ds-tag">STEP 02</p>
                <h3 className="ds-title">검진상품 / 병원 선택</h3>
                <p className="ds-desc">
                  검진상품 또는 병원을 기준으로 시작할 수 있어요.
                  <br />
                  나에게 편한 방식으로 검진을 진행해보세요.
                </p>
              </div>
              <div className="ds-media double">
                <img src={step3_2} alt="검진상품 및 병원 선택 화면" />
              </div>
            </div>

            <div className="ds-col ds-col--right">
              <div className="ds-media">
                <img src={step4} alt="검진상품 비교 화면" />
              </div>
              <div className="ds-text">
                <p className="ds-tag">CHECK LIST</p>
                <h3 className="ds-title">검진상품 비교</h3>
                <p className="ds-desc">
                  선택한 상품의 검진 항목을 한눈에 비교할 수 있어요.
                  <br />
                  다른 상품을 선택해 검사 항목 차이를 바로 확인하세요.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* 3묶음: STEP 03 / STEP 06 */}
        <section className="dual-showcase" data-reveal="dual">
          <div className="ds-col ds-col--left">
            <div className="ds-text">
              <p className="ds-tag">STEP 03</p>
              <h3 className="ds-title">검진일 선택 · 정보 입력</h3>
              <p className="ds-desc">
                검진 날짜와 예약자 정보를 간단히 입력하면 돼요.
                <br />
                동의서 작성까지 한 번에 완료됩니다.
              </p>
            </div>
            <div className="ds-media triple">
              <img src={step5} alt="정보 입력 화면" />
            </div>
          </div>

          <div className="ds-col ds-col--right">
            <div className="ds-media">
              <img src={step6_1} alt="예약 완료 안내 화면" />
            </div>
            <div className="ds-text">
              <p className="ds-tag">STEP 06</p>
              <h3 className="ds-title">예약 완료</h3>
              <p className="ds-desc">
                예약이 완료됐어요.
                <br />
                입력한 정보를 확인하고, 안내 문자를 받아보세요.
              </p>
            </div>
          </div>
        </section>

        {/* ===== News Section ===== */}
        <section
          className="third-section"
          data-reveal-group
          data-reveal-base="60"
          data-reveal-step="70"
        >
          {/* <div className="news-tagline">
            <span>MEDICODE</span>
            <span>NEWS</span>
          </div> */}

          <div className="third-section-inner media-grid">
            {/* LEFT */}
            <div className="media-left" data-reveal>
              <div className="eyebrow">
                <span className="dot"></span>
                <span className="label">NEWS</span>
              </div>
              <h2 className="media-title">다양한 소식을 전해드립니다</h2>
              <p className="media-sub">
                메디코드는 새로운 소식과 유용한 정보를 신뢰감 있게 전하겠습니다.
              </p>
              <div className="social-head">
                <span className="social-title">THUMBNAIL</span>
                <div className="social-ctrl">
                  <button
                    type="button"
                    className="ctrl-btn prev"
                    aria-label="prev"
                    onClick={handlePrev}
                  />
                  <button
                    type="button"
                    className="ctrl-btn next"
                    aria-label="next"
                    onClick={handleNext}
                  />
                  <button
                    type="button"
                    className="ctrl-btn plus"
                    aria-label="more"
                    onClick={() => navigate("/board/news")}
                  />
                </div>
              </div>
              <div
                className="social-card"
                onClick={() =>
                  handleDetailClick("news", start + selectedInPage)
                }
                role="button"
              >
                {selectedNews?.img1 ? (
                  <img
                    src={`https://www.medicode.co.kr:8445/uploads/${selectedNews.img1}`}
                    alt={selectedNews.title}
                    loading="lazy"
                  />
                ) : (
                  <div className="thumb-placeholder" />
                )}
              </div>
            </div>

            {/* RIGHT */}
            <div className="media-right" data-reveal>
              <ul className="news-list">
                {visibleNews.map((news, i) => {
                  const d = new Date(news.date);
                  const ym = `${d.getFullYear()}.${String(
                    d.getMonth() + 1
                  ).padStart(2, "0")}`;
                  const day = String(d.getDate()).padStart(2, "0");
                  const isActive = i === selectedInPage;

                  return (
                    <li
                      key={`${start + i}`}
                      className={`news-row ${isActive ? "active" : ""}`}
                      onClick={() => setSelectedInPage(i)}
                      role="button"
                    >
                      <div className="news-date">
                        <span className="ym">{ym}</span>
                        <span className="day">{day}</span>
                      </div>
                      <div className="news-meta">
                        <span className="badge-kind">
                          {news.kind || "보도자료"}
                        </span>
                        <h3 className="news-title">{news.title}</h3>
                      </div>
                      <div
                        className="news-chev"
                        aria-hidden
                        onClick={() => handleDetailClick("news", start + i)}
                      >
                        ›
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>
      </main>

      {/* ===== Split Hero ===== */}
      <section className="split-hero">
        <div className="split-hero__divider" aria-hidden />
        <article
          className="split-hero__panel split-hero__panel--left"
          style={{ backgroundImage: "url(/assets/images/cta-hospital.jpg)" }}
        >
          <div className="split-hero__overlay__left" />
          <div className="split-hero__inner align-left">
            <div className="split-hero__eyebrow__left">
              <span className="dot" /> HOSPITAL
            </div>
            <h3 className="split-hero__title__left">
              검증된 검진 플랫폼과 함께
              <br />
              병원의 가치를 넓히세요
            </h3>
            <p className="split-hero__desc__left">
              신뢰 중심의 검진 네트워크로 병원과 고객을 연결하는 플랫폼입니다.
            </p>
            <div className="split-hero__actions">
              <Link
                to="/board/partnership"
                state={{ scrollTo: "hospital-section" }}
                className="pill pill--solid"
              >
                협력병원
              </Link>
              <a className="pill pill--ghost__left" href="/board/contact_us">
                문의하기
              </a>
            </div>
          </div>
        </article>

        <article
          className="split-hero__panel split-hero__panel--right"
          style={{ backgroundImage: "url(/assets/images/cta-partner.jpg)" }}
        >
          <div className="split-hero__overlay__right" />
          <div className="split-hero__inner align-right">
            <div className="split-hero__eyebrow__right">
              <span className="dot" /> PARTNER
            </div>
            <h3 className="split-hero__title__right">
              웨딩 · 채널 <br />
              협력업체 모집
            </h3>
            <p className="split-hero__desc__right">
              고객 만족과 수익을 함께 만드는 검진 제휴 시스템
            </p>
            <div className="split-hero__actions">
              <Link
                to="/board/partnership"
                state={{ scrollTo: "partner-section" }}
                className="pill pill--solid"
              >
                협력업체
              </Link>
              <Link
                to="/medicode"
                state={{ scrollTo: "bottom" }}
                className="pill pill--ghost__right"
              >
                제안서 확인
              </Link>
            </div>
          </div>
        </article>
      </section>
    </Fragment>
  );
};

export default Main;
